<template>
  <div class="pagination-wrapper">
    <div v-if="totalPages > 1">
      <v-pagination
        v-model="currentPage"
        :length="totalPages"
        :total-visible="totalVisiblePages"
        density="compact"
        class="custom-pagination"
        @update:model-value="handlePageChange"
      >
        <template v-slot:item="{ page, props, isActive }">
          <button
            v-bind="props"
            class="pagination-item-button"
            :class="{ 'is-active': isActive }"
            :aria-current="isActive ? 'page' : undefined"
          >
            {{ page }}
          </button>
        </template>

        <template v-slot:prev="{ onClick, disabled }">
          <button
            class="pagination-nav-button"
            :disabled="disabled"
            :aria-disabled="disabled"
            aria-label="Previous page"
            @click="onClick"
          >
            <ChevronLeftIcon aria-hidden="true" />
          </button>
        </template>

        <template v-slot:next="{ onClick, disabled }">
          <button
            class="pagination-nav-button"
            :disabled="disabled"
            :aria-disabled="disabled"
            aria-label="Next page"
            @click="onClick"
          >
            <ChevronRightIcon aria-hidden="true" />
          </button>
        </template>
      </v-pagination>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from "vue";
import { useDisplay } from "vuetify";
import ChevronRightIcon from "../icons/ChevronRightIcon.vue";
import ChevronLeftIcon from "../icons/ChevronLeftIcon.vue";

const props = defineProps({
  modelValue: {
    type: Number,
    required: true,
  },
  totalPages: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(["update:modelValue"]);
const { mobile } = useDisplay();

const currentPage = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const totalVisiblePages = computed(() => (mobile.value ? 5 : 7));

function handlePageChange(newPage) {
  emit("update:modelValue", newPage);
}
</script>

<style scoped>
.pagination-wrapper {
  margin-top: 2px;
  width: 100%;
}

/* Vuetify pagination list customization */
.custom-pagination :deep(.v-pagination__list) {
  gap: 8px;
  justify-content: center;
  padding: 0;
  flex-wrap: nowrap;
}

.custom-pagination :deep(.v-pagination__item),
.custom-pagination :deep(.v-pagination__prev),
.custom-pagination :deep(.v-pagination__next) {
  margin: 0 !important;
}

/* Page Numbers style */
.pagination-item-button {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  border: 1px solid var(--neutral-700);
  background-color: var(--white);
  color: var(--neutral-700);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-item-button.is-active {
  background-color: var(--primary-blue);
  color: var(--white);
  border-color: var(--primary-blue);
}

.pagination-item-button:hover:not(.is-active) {
  background-color: var(--white-hover-1);
}

/* Navigation buttons (Prev/Next) style */
.pagination-nav-button {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid transparent;
  background-color: transparent;
  color: var(--neutral-700);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.pagination-nav-button:hover:not(:disabled) {
  background-color: var(--white-hover-1);
}

.pagination-nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* WCAG 2.4.7 Focus Visible */
.pagination-item-button:focus-visible,
.pagination-nav-button:focus-visible {
  outline: 3px solid var(--primary-blue);
  outline-offset: 2px;
}

/* Ellipsis dots */
.custom-pagination :deep(.v-pagination__more) {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--neutral-700);
  font-weight: 700;
  font-family: var(--font-family-primary);
}

/* Larger gap for nav buttons if possible */
.custom-pagination :deep(.v-pagination__prev) {
  margin-right: 12px !important;
}

.custom-pagination :deep(.v-pagination__next) {
  margin-left: 12px !important;
}

@media (max-width: 600px) {
  .custom-pagination :deep(.v-pagination__list) {
    gap: 4px;
  }

  .pagination-item-button,
  .pagination-nav-button,
  .custom-pagination :deep(.v-pagination__more) {
    width: 36px;
    height: 36px;
    font-size: var(--fs-small);
  }

  .custom-pagination :deep(.v-pagination__prev) {
    margin-right: 4px !important;
  }

  .custom-pagination :deep(.v-pagination__next) {
    margin-left: 4px !important;
  }
}
</style>
